import { useMemo } from 'react';
import { Button, Flex, Image, Text, useColorMode } from '@chakra-ui/react';
import { 
  GoogleLogin, 
  CredentialResponse,
} from '@react-oauth/google';

import { useSignInContext } from '../../context/SignIn';

import { useFailureToast } from '../../hooks/useToast';
import useDocumentTitle from '../../hooks/useDocumentTitle';

import { login } from '../../apis/auth';

import ProplessFC from '../../types/ProplessFC';

import styleGenerator from './styles';

// Helper function to decode JWT token to get user profile
const decodeJwt = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

const SignInModal: ProplessFC = () => {
  useDocumentTitle('Sign In');
  const { setIsSignedIn } = useSignInContext();

  const { colorMode } = useColorMode();
  const styleProps = useMemo(
    () => styleGenerator(colorMode === 'dark'),
    [colorMode],
  );

  const failureToast = useFailureToast();

  const signIn = async (tokenId: string, signedInCallback: Function) => {
    // TODO: Refactor this using react-query
    try {
      // TODO: Define interface for this and other requests' response.
      const {
        status,
        data: { data: authData },
      } = await login(tokenId);

      if (status === 200) {
        // TODO: Change this to camelcase at all places.
        localStorage.setItem('auth_token', authData.auth_token);
        localStorage.setItem('scopes', JSON.stringify(authData.scopes));
        signedInCallback(true);
      } else {
        failureToast('Could not connect to the server');
      }
    } catch (error) {
      console.error(error);
      failureToast('Could not connect to the server');
    }
  };

  const successHandler = (credentialResponse: CredentialResponse) => {
    if (credentialResponse.credential) {
      // Decode JWT to get user profile info
      const profile = decodeJwt(credentialResponse.credential);
      if (profile && profile.email) {
        localStorage.setItem('email', profile.email);
        // Use the JWT credential (tokenId) as expected by the backend
        signIn(credentialResponse.credential, setIsSignedIn);
      } else {
        failureToast('Could not get user information');
      }
    }
  };

  const errorHandler = () => {
    console.error('Google Login Failed');
    failureToast('Google Login Failed');
  };

  return (
    <Flex {...styleProps.modalWrapper}>
      <Flex {...styleProps.modal}>
        <Text {...styleProps.title}>Welcome to JARVIS</Text>
        <Text {...styleProps.signInHeading}>Sign in</Text>
        
        {/* Google Login with standard styling */}
        <GoogleLogin
          onSuccess={successHandler}
          onError={errorHandler}
          useOneTap={false}
          auto_select={false}
          size="large"
          theme={colorMode === 'dark' ? 'filled_black' : 'outline'}
          text="continue_with"
          width="320"
        />
      </Flex>
    </Flex>
  );
};

export default SignInModal;

